import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownButton, Nav, Navbar } from 'react-bootstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { IUser } from '@/store/types';

import { Mobile, TabletAndDesktop } from '@/breakpoints-config/breakpoints';
import { TimeTooltip } from '@/components/common/Tooltip';
import { UserType } from '@/store/enums';
import { ClientSelectionCombo } from '@/components/client/ClientSelectionCombo';
import DriftChat from '@/pages/agent/driftChat';
import { otherColors } from '@/styles/colors';
import { useAppDispatch, useAppSelector, useOngoingShift, useTimezone } from '@/hooks';
import routes from '@/config/routes';
import { changeMenuType } from '@/store/slices/authSlice';
import { NavigationItems } from '@/components/layout/navigation';
import { canshowreferralmenu } from '@/store/slices/userSlice';
import { getFeatureFlags } from '@/store/slices/featureFlags/featureFlagsSlice';
import { IFeatureFlag } from '@/store/slices/featureFlags/types';
import { AxiosResponse } from 'axios';

interface IProps {
  user: IUser;
  menuType: UserType;
  userType: UserType;
  toggleExpand: () => void;
  onCollapse: () => void;
}

const AuthedNavigation = ({ user, menuType, userType, toggleExpand, onCollapse }: IProps): React.ReactElement => {
  const [showReferFriendMenu, setShowReferFriendMenu] = useState(false);
  const { t } = useTranslation();
  const { timezoneAbbreviation } = useTimezone();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const hasActiveGigs = Boolean(user?.hasActiveGigs);
  const availableClients = user?.availableClients;
  const isReferAFriendEnabled = useAppSelector(state => state.featureFlags.featureFlags.isReferAFriendEnabled);
  const [featureFlags, setFeatureFlags] = useState<IFeatureFlag | AxiosResponse<any> | undefined>({} as IFeatureFlag);

  const { officialTime } = useOngoingShift();

  useEffect(() => {
    dispatch(canshowreferralmenu()).then(res => {
      setShowReferFriendMenu(res.payload === true ? true : false);
    });
  }, [user, userType, menuType]);

  useEffect(() => {
    dispatch(getFeatureFlags()).then(res => {
      setFeatureFlags(res.payload);
    });
  }, [user, userType, menuType]);

  const handleSignOut = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    sessionStorage.removeItem('MobNumVerificationShown');
    sessionStorage.removeItem('showEsignPopup');
    history.push(routes.auth.logout);
  };

  const onMyProfileClicked = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    history.push(routes.sp.scheduling.user.profile.index);
  };
  const onReferFriendMenuClicked = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    history.push(routes.sp.referralProgram);
  };

  const onMenuTypeChanged = (menuType: UserType) => {
    dispatch(changeMenuType(menuType)).then(() => {
      switch (menuType) {
        case UserType.client:
          return history.push(routes.client.scheduling.index);
        case UserType.agent:
          return history.push(routes.sp.scheduling.dashboard);
        case UserType.admin:
          return history.push(routes.admin.gig.list);
        case UserType.unauthorized:
        default:
          return history.push(routes.root);
      }
    });
  };

  return (
    <>
      <div className="d-flex">
        <Mobile>
          <TimeTooltip placement="left" showTime={250} hideTime={500}>
            <Time className="mobile">
              <p className="time">{officialTime.time}</p>
              <p className="time-zone">{timezoneAbbreviation}</p>
            </Time>
          </TimeTooltip>
        </Mobile>
        <Navbar.Toggle aria-controls="basic-navbar-nav" onClick={toggleExpand} />
      </div>
      <Navbar.Collapse id="basic-navbar-nav" className="bg-white">
        <Nav className="me-auto align-items-center">
          <NavigationItems
            menuType={menuType}
            hasActiveGigs={hasActiveGigs}
            onCollapseNavItem={onCollapse}
            featureFlags={featureFlags as IFeatureFlag}
          />
        </Nav>
        {menuType === UserType.client && <ClientSelectionCombo onSelect={onCollapse} />}
        <TabletAndDesktop>
          <TimeTooltip placement="left" showTime={250} hideTime={500}>
            <Time>
              <p className="time">{officialTime.time}</p>
              <p className="time-zone">{timezoneAbbreviation}</p>
            </Time>
          </TimeTooltip>
        </TabletAndDesktop>
        {userType === UserType.agent && <DriftChat onClick={onCollapse} />}
        <ProfilePicDropDown id="dropdown-profile-button" title={user.firstName.charAt(0).toUpperCase()} align="end">
          {menuType !== UserType.client && (availableClients?.length ?? 0) > 0 && (
            <Dropdown.Item
              onClick={() => onMenuTypeChanged(UserType.client)}
              onSelect={onCollapse}
            >{t`navbar.serviceUser`}</Dropdown.Item>
          )}
          {userType === UserType.admin && menuType !== UserType.admin && (
            <Dropdown.Item
              onClick={() => onMenuTypeChanged(UserType.admin)}
              onSelect={onCollapse}
            >{t`navbar.admin.title`}</Dropdown.Item>
          )}
          {userType === UserType.agent && menuType !== UserType.agent && (
            <Dropdown.Item
              onClick={() => onMenuTypeChanged(UserType.agent)}
              onSelect={onCollapse}
            >{t`navbar.serviceProvider`}</Dropdown.Item>
          )}
          <Dropdown.Item onClick={onMyProfileClicked} onSelect={onCollapse}>{t`navbar.myProfile`}</Dropdown.Item>
          {userType === UserType.agent && isReferAFriendEnabled && showReferFriendMenu && (
            <Dropdown.Item
              onClick={onReferFriendMenuClicked}
              onSelect={onCollapse}
            >{t`navbar.referFriend`}</Dropdown.Item>
          )}
          <Dropdown.Item onClick={handleSignOut} onSelect={onCollapse}>{t`navbar.btnSignOut`}</Dropdown.Item>
        </ProfilePicDropDown>
      </Navbar.Collapse>
    </>
  );
};

export { AuthedNavigation };

const ProfilePicDropDown = styled(DropdownButton)`
  button {
    border-radius: 100%;
    width: 45px;
    height: 45px;
    line-height: 45px;
    font-size: 24px;
    padding: 0;
    margin-right: 10px;
    background-color: ${otherColors.lxc.blue};
    &::after {
      content: none;
    }
  }
`;

const Time = styled.div`
  margin-right: 14px;
  cursor: pointer;
  .time {
    color: ${otherColors.outer_space};
    font-size: 16px;
    margin: 0;
  }
  .time-zone {
    color: ${otherColors.dark_grey};
    font-size: 13px;
    margin: 0;
    text-align: end;
  }
  &.mobile {
    margin-right: 8px;
    .time {
      font-size: 13px;
    }
    .time-zone {
      font-size: 10px;
    }
  }
`;
